import {ProjectPriceMode} from "@natomas-org/core";
import {fetchVisualBudgetTable} from "../../components/portal/views/ProposalBudgetView/api/fetchBudget";
import {setBudget} from "../../components/_shared/slices/BudgetSlice";
import useActiveProject from "../../components/_shared/hooks/useProject/useActiveProject";
import {useCartItems} from "../../components/_shared/hooks/useCart/useCartItem/useCartItems";
import {useDispatch, useSelector} from "react-redux";
import {Dispatch, useEffect, useRef} from "react";
import {IStore} from "../../components/_shared/slices/types/Store";
import {auth} from "../../database/firebase";
import {useCurrentUser} from "../../components/_shared/hooks/useCurrentUser";

export const BudgetSliceManager = () => {
  const {
    projectPriceMode,
    cartItemIds,
    id,
    isCartEnabled,
    phase1Micros,
    phase2Micros,
  } = useActiveProject();
  const cartItems = useCartItems(cartItemIds);
  const budgets = useSelector((state: IStore) => state.budget);
  const projectsState = useSelector((state: IStore) => state.project);
  const { loggedInUser, authInitialized } = useCurrentUser();
  const dispatch = useDispatch();
  const hasFetched = useRef(false);
  
  useEffect(() => {
    if (hasFetched.current) {
      return;
    }
    
    let errMsg: string | undefined = undefined;
    
    // Authentication checks first
    if (!authInitialized) {
      errMsg = "Authentication not initialized";
    }
    if (!auth.currentUser) {
      errMsg = "No authenticated user";
    }
    if (!loggedInUser?.uid) {
      errMsg = "No logged in user";
    }
    
    // Project validation checks
    if (!id) {
      errMsg = "No project id";
    }
    if (!projectsState || !projectsState[id]) {
      errMsg = "Project not loaded in state";
    }
    if (!projectPriceMode) {
      errMsg = "No price mode";
    }
    if (!cartItems) {
      errMsg = "No cart items";
    }
    if (budgets[id] === null) {
      errMsg = "Budget is being fetched";
    }
    if (isCartEnabled === undefined) {
      errMsg = "Cart enabled is undefined";
    }
    if (
      projectPriceMode === ProjectPriceMode.CostPlus &&
      (phase1Micros === undefined || phase2Micros === undefined)
    ) {
      errMsg = "phase micros undefined";
    }
    
    if (errMsg) {
      // Only log warnings for unexpected conditions, not normal startup timing
      const isNormalStartupTiming = !authInitialized || !id || !projectsState || !projectsState[id];
      
      if (!isNormalStartupTiming) {
        console.warn(`⚠️ Budget fetch skipped: ${errMsg}`, {
          authInitialized,
          hasCurrentUser: !!auth.currentUser,
          hasLoggedInUser: !!loggedInUser?.uid,
          projectId: id,
          projectPriceMode,
          cartItemsLength: cartItems?.length,
          isCartEnabled,
          phase1Micros,
          phase2Micros
        });
      }
      return;
    }
    
    hasFetched.current = true;
    refreshBudget(id, dispatch).then((r) => {
      // Budget refresh completed
    });
  }, [
    authInitialized,
    loggedInUser,
    budgets,
    cartItems,
    dispatch,
    id,
    isCartEnabled,
    phase1Micros,
    phase2Micros,
    projectPriceMode,
    projectsState,
  ]);

  return null;
};

export const refreshBudget = async (
  projectId: string,
  dispatch: Dispatch<any>
): Promise<string> => {
  const id = projectId;
  
  // Additional auth check before making the API call
  if (!auth.currentUser) {
    const errorMsg = "Cannot refresh budget: No authenticated user";
    console.error(`🔴 ${errorMsg}`);
    throw new Error(errorMsg);
  }
  

  
  dispatch(
    setBudget({
      projectId: id,
      entries: null,
    })
  );
  
  return fetchVisualBudgetTable({
    projectId: id,
    section: null,
  })
    .then((entries) => {
  
      dispatch(
        setBudget({
          projectId: id,
          entries,
        })
      );
      return "success";
    })
    .catch((e) => {
      // Check if this is a "project not found" error which might be expected
      const isProjectNotFoundError = e?.error?.error === 'Failed to get project information';
      
      if (isProjectNotFoundError) {
        console.warn(`⚠️ Budget fetch: Backend service cannot find project ${id} (known backend issue)`, {
          projectId: id,
          userId: auth.currentUser?.uid,
          backendError: e?.error?.error,
          note: "Project exists in Firestore but backend service cannot access it"
        });
      } else {
        console.error(`🔴 Budget fetch failed for project ${id}:`, e);
      }
      
      dispatch(
        setBudget({
          projectId: id,
          entries: [],
        })
      );
      return e;
    });
};
